import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import moment from 'moment';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import { DateFormat } from '../../utils/date';
import { formatPluralDuration } from '../../utils/durationUnit';
import CollectionName from '../../utils/collections';
import { openFirestoreDocument } from '../../utils/support';
import {
  ContractStatus,
  FUTURE_CONTRACT_STATUSES,
} from '../../CoachTools/utils/userContract';
import { canAmendContract } from '../../CoachTools/utils/billingTarget';
import { getUserStatus } from '../../CoachTools/utils/userStatus';
import useSessionStore from '../../hooks/useSessionStore';
import ContractDetails from '../ContractDetails';
import Accordion from '../Accordion';

import {
  Container,
  ContractInfoKey,
  ContractInfoLine,
  ContractInfoValue,
  StyledOnCallActions,
  HeaderActions,
  HeaderActionButton,
} from './styles';
import texts from './texts.json';

const MONTHLY_SUBSCRIPTION = 1;

const ContractHistory = ({
  contractDocs,
  billingTargets,
  onOpenContractModal,
  isBlockedByFutureContract,
}) => {
  const { isOnCallUser } = useSessionStore();

  const renderContractInfo = (contract) => {
    /*
      We have to determine the actual startDate to use. If the contract has a subscriptionCreatedAt property, it means
      the subscription already started. Otherwise, we use the startDate property, which will point to the start date
      of future startDates.
    */
    const processedStartDate = contract.subscriptionCreatedAt || contract.startDate;

    /*
      The info lines are extra lines we render at the top of the contract accordion. We show multiple properties like
      the startDate, the initial payment and the monthly payment.
    */
    const infoLines = [
      {
        key: texts.info.since.key,
        value: moment(processedStartDate).format(DateFormat.TEXT_DATE_FORMAT),
      },
      {
        key: texts.info.initialPayment.key,
        value: format(texts.info.initialPayment.value, {
          amount: contract.initialPaymentInCentsStr,
          extra: contract.initialTerm > MONTHLY_SUBSCRIPTION
            ? format(texts.pif, {
              duration: formatPluralDuration(contract.initialTerm, contract.durationUnit),
            })
            : '',
        }),
      },
      ...(contract.challengeId ? [] : [
        {
          key: texts.info.monthlyPayment.key,
          value: format(texts.info.monthlyPayment.value, {
            amount: contract.totalPriceInCentsStr,
            extra: format(texts.monthlyStart, {
              date: moment(processedStartDate).add(contract.initialTerm, 'months').format(DateFormat.TEXT_DATE_FORMAT),
            }),
          }),
        },
      ]),
    ];

    return (
      <>
        {infoLines.map((line) => (
          <ContractInfoLine key={line.key}>
            <ContractInfoKey>{`${line.key}:`}</ContractInfoKey>
            <ContractInfoValue>{line.value}</ContractInfoValue>
          </ContractInfoLine>
        ))}
      </>
    );
  };

  // Let's sort the contracts by creation date, so we can show the most recent contracts first.
  const sortedContracts = contractDocs.slice().sort((a, b) => b.createdAt - a.createdAt);
  const targetFor = (contract) => billingTargets.find(({ contractId }) => contractId === contract.id);

  const shouldShowUpdateTermsAction = (contract) => {
    const isPendingContract = contract.status === ContractStatus.PENDING;

    if (contract.current) {
      // A contract with no entry on the user doc has no state to judge it by.
      const target = targetFor(contract);
      return !!target && canAmendContract(target) && !isBlockedByFutureContract(target.contractId, target.productId);
    }

    return isPendingContract;
  };

  const getActionIsAmendment = (contract) => {
    const isCurrentActiveContract = contract.current
      && (contract.status === ContractStatus.ACTIVE || contract.status === ContractStatus.TRIALING);
    return isCurrentActiveContract ? true : !!contract.isAmendment;
  };

  // A pending amendment is being edited rather than replaced, so it supersedes whatever it
  // already points at.
  const getSupersededContractId = (contract) => {
    if (contract.current) {
      return contract.id;
    }
    return contract.isAmendment ? (contract.originalContract || '') : '';
  };

  const handleUpdateTermsClick = (event, contract) => {
    event.stopPropagation();
    onOpenContractModal(
      getActionIsAmendment(contract),
      getSupersededContractId(contract),
      targetFor(contract)?.productId ?? contract.productId ?? '',
    );
  };

  const getLabel = (contract) => {
    if (contract.isAmendment) {
      return texts.amendmentLabel;
    }
    if (contract.primaryContractId) {
      return format(texts.secondaryContractLabel, {
        discount: contract.price.initialDiscountPercentage,
      });
    }
    if (
      FUTURE_CONTRACT_STATUSES.includes(contract.status)
    ) {
      return format(texts.startDateLabel, {
        date: moment(contract.startDate).format(DateFormat.CONTRACT_DATE_FORMAT),
      });
    }
    return '';
  };

  const distinctProductCount = new Set(billingTargets.map(({ productId }) => productId).filter(Boolean)).size;

  const getTitle = (contract) => {
    const productSuffix = distinctProductCount > 1 && contract.productName
      ? format(texts.productSuffix, { productName: contract.productName })
      : '';
    let base = texts.expiredContract;
    if (contract.current) {
      const clientView = targetFor(contract)?.clientView;
      const hasState = !!clientView && (clientView.isActive || !!clientView.subscriptionStatus);
      const status = hasState ? getUserStatus(clientView).status : contract.status;
      base = format(texts.currentContract, { status: status?.split('_').join(' ').toUpperCase() || '' });
    } else if (FUTURE_CONTRACT_STATUSES.includes(contract.status)) {
      base = format(texts.futureContract, { status: contract.status?.split('_').join(' ').toUpperCase() });
    }
    return `${base}${productSuffix}`;
  };

  return (
    <Container>
      {sortedContracts.map((contract) => {
        const title = getTitle(contract);

        return (
          <Accordion
            key={contract.id}
            title={title}
            label={getLabel(contract)}
            defaultExpanded={contract.current}
            actions={shouldShowUpdateTermsAction(contract) ? (
              <HeaderActions>
                <HeaderActionButton
                  size="small"
                  onClick={(event) => handleUpdateTermsClick(event, contract)}
                  onFocus={(event) => event.stopPropagation()}
                >
                  {texts.updateTerms}
                </HeaderActionButton>
              </HeaderActions>
            ) : null}
          >
            {renderContractInfo(contract)}
            <ContractDetails
              contractTerms={contract.terms}
              coachConditions={contract.coachConditions}
              userId={contract.id}
              isAmendment={contract.isAmendment}
            />
            {isOnCallUser && (
              <StyledOnCallActions
                actions={[
                  {
                    onClick: () => openFirestoreDocument(`${CollectionName.USER_CONTRACT}/${contract.id}`),
                    disabled: !contract.id,
                    label: texts.support.contractDoc,
                  },
                ]}
              />
            )}
          </Accordion>
        );
      })}
    </Container>
  );
};

ContractHistory.propTypes = {
  contractDocs: PropTypes.arrayOf(PropTypes.object),
  billingTargets: PropTypes.arrayOf(PropTypes.object),
  onOpenContractModal: PropTypes.func,
  isBlockedByFutureContract: PropTypes.func,
};

ContractHistory.defaultProps = {
  contractDocs: [],
  billingTargets: [],
  onOpenContractModal: () => {},
  isBlockedByFutureContract: () => false,
};

export default compose(
  observer,
)(ContractHistory);
